import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { from, Observable, of, Subscription } from 'rxjs';
import { concatMap, finalize, mergeMap, toArray } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { DateTimeSchedule, RangeValidation } from 'src/app/shared/components/date-time-schedule-modal/shared/models';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { SiteV2 } from 'src/app/shared/models';
import { Editor2Answer, Editor2Attachment } from 'src/app/shared/models/misc.model';
import { API_URL_PREFIX, BasicService } from 'src/app/shared/services/basic.service';
import { DropdownItemsService } from 'src/app/shared/services/dropdown-items.service';
import DateUtils from 'src/app/shared/utils/date.utils';

import { PublicationsService } from '../services/publications.service';
import { ImgAltText, Publication, PublicationPayload } from '../shared/models';

@Component({
  selector: 'app-publication-edit',
  templateUrl: './publication-edit.component.html',
  styleUrls: ['./publication-edit.component.scss'],
})
export class PublicationEditComponent implements OnInit, OnDestroy {
  @Input() isDuplication: boolean;
  @Input() parentPublication: Publication; // only for twitter, but not if the parent is already a retweet
  @Input() publicationToEdit: Publication;
  @Output() closeDialog: EventEmitter<Publication> = new EventEmitter();

  @ViewChild('publicationEditTemplateRef', { static: true }) publicationEditTemplateRef: TemplateRef<any>;

  attachmentBaseUrl = `${API_URL_PREFIX}/${this.alcmeonContextService.companyId}/pictures/`;
  dialogRef: MatDialogRef<any>;
  displayDateTimeModal = false;
  editorInitialContent: Editor2Answer[];
  hasAttachements = false;
  imgAltText: string[] = [];
  isFacebook = false;
  isTwitter = false;
  rangeValidation: RangeValidation = null;
  selectedSite: SiteV2;
  sites: Choice[];
  title = '';

  // Avoid error NG0100 ExpressionChangedAfterItHasBeenCheckedError on "form.invalid" because of editor (not in creation, just in edition, because the form is valid after init of editor)
  // Others buttons with "real" *ngIf don't have the error.
  dummyTrue = true;

  form = this.formBuilder.group({
    site: ['', Validators.required],
    editors: [[], Validators.required],
    attachedLink: '',
    imgAltTextFormArray: this.formBuilder.array([]),
    internalNote: '',
  });

  get imgAltTextFormArray() {
    return this.form.get('imgAltTextFormArray') as FormArray;
  }

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private dialog: MatDialog,
    private dropdownItemsService: DropdownItemsService,
    private formBuilder: FormBuilder,
    private publicationsService: PublicationsService,
    private sitesService: SitesService
  ) {}

  ngOnInit(): void {
    const sitesToPublish = this.publicationsService.getSitesToPublish();

    this.sites = this.dropdownItemsService.buildSiteItems(sitesToPublish);

    this.subscriptions.add(
      this.form.get('site').valueChanges.subscribe(site => {
        if (site) {
          this.selectedSite = this.sitesService.sitesV2Map.get(site);
          this.isFacebook = this.selectedSite.typeName === SiteTypeEnum.Facebook;
          this.isTwitter = this.selectedSite.typeName === SiteTypeEnum.Twitter;

          if (this.isFacebook) {
            // scheduled_publish_time
            //
            // here it says between 10 min and 30 days
            // https://developers.facebook.com/docs/pages-api/posts/#ajout-de-publications
            //
            // here it says between 10 min and 75 days
            // https://developers.facebook.com/docs/graph-api/reference/v20.0/page/feed#pubfields
            //
            // by testing it seems to be 29 days minus 1h (test in summer time, check in winter if there is this weird 1h diff)
            // we do not consider 1h diff in rangeValidation
            this.rangeValidation = { min: { amount: 10, unit: 'minutes' }, max: { amount: 29, unit: 'days' } };
          } else {
            this.rangeValidation = null;
          }
        }
      })
    );

    if (this.sites.length === 1) {
      this.form.get('site').setValue(this.sites[0].id);
    }

    this.title = 'publications/edition/title/new';

    if (this.publicationToEdit) {
      this.title = 'publications/edition/title/edit';
      this.initFormWithPublication();
    }
    // "else if" because can't save an "answer" to a parent publication
    else if (this.parentPublication) {
      this.title = 'publications/edition/title/response';
      this.form.get('site').setValue(this.parentPublication.site.id);
      this.form.get('site').disable();
    }

    if (this.isDuplication) {
      // reset publication after all inits to force create new one
      this.publicationToEdit = null;
      this.title = 'publications/edition/title/new';
    }

    this.subscriptions.add(
      this.form.get('editors').valueChanges.subscribe((editors: Editor2Answer[]) => {
        // images alt texts are only for twitter
        if (this.isTwitter) {
          const allAttachementsIds = [];

          editors?.forEach((editor: Editor2Answer) => {
            editor.attachments.forEach(attachment => {
              allAttachementsIds.push(attachment.value.pictureId);

              const isAttachmentAlreadyInForm = this.imgAltTextFormArray.value.some((imgAltText: ImgAltText) => {
                return imgAltText.id === attachment.value.pictureId;
              });

              if (!isAttachmentAlreadyInForm) {
                const mediaAlt = this.formBuilder.group({
                  id: attachment.value.pictureId,
                  altText: '',
                } as ImgAltText);
                this.imgAltTextFormArray.push(mediaAlt, { emitEvent: false });
              }
            });
          });

          const imgAltTextToDelete = [];

          this.imgAltTextFormArray.value.forEach((imgAltText: ImgAltText, index: number) => {
            if (!allAttachementsIds.includes(imgAltText.id)) {
              imgAltTextToDelete.push(index);
            }
          });

          // start from the end to have all indexes always correct
          imgAltTextToDelete.reduceRight((acc, index) => this.imgAltTextFormArray.removeAt(index), null);
        }

        // setTimeout prevents NG0100: ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.hasAttachements = !!editors[0].attachments?.length;
        });
      })
    );

    this.dialogRef = this.dialog.open(this.publicationEditTemplateRef, {
      autoFocus: false,
      disableClose: true,
      panelClass: ['large-size'],
    });

    this.dialogRef.afterClosed().subscribe(publication => {
      this.closeDialog.emit(publication);
    });
  }

  /**
   * If publicationToEdit, it can be draft not modified (no impact)
   * or, it's after an error, so it allows to go in the correct tab after close.
   */
  cancel() {
    this.dialogRef.close(this.publicationToEdit);
  }

  initFormWithPublication() {
    this.form.get('site').setValue(this.publicationToEdit.site.id);

    if (this.publicationToEdit.last_revision.internal_note?.text) {
      this.form.get('internalNote').setValue(this.publicationToEdit.last_revision.internal_note.text);
    }

    if (this.publicationToEdit.last_revision.attached_link?.url) {
      this.form.get('attachedLink').setValue(this.publicationToEdit.last_revision.attached_link.url);
    }

    this.editorInitialContent = this.publicationToEdit.last_revision.content.map((message, index) => ({
      attachments: this.publicationToEdit.last_revision.medias[index].map(media => {
        if (media.type === 'picture') {
          const mediaAlt = this.formBuilder.group({
            id: media.id,
            altText: media.alt,
          } as ImgAltText);
          this.imgAltTextFormArray.push(mediaAlt);
        }

        return {
          type: media.type,
          value: {
            pictureId: media.type === 'picture' ? media.id : null,
            videoId: media.type === 'video' ? media.id : null,
          },
          mimeType: media.mime_type,
        };
      }),
      links: null,
      text: message,
    }));
  }

  private prepareData(): PublicationPayload {
    const { editors, attachedLink, imgAltTextFormArray, internalNote } = this.form.value;

    const data: PublicationPayload = {
      editor_content: editors,
      pictures_alt: imgAltTextFormArray.reduce((acc: { [id: number]: string }, imgAltText: ImgAltText) => {
        return { ...acc, ...{ [imgAltText.id]: imgAltText.altText } };
      }, {}),
      internal_note: { text: internalNote },
      attached_link: { url: attachedLink },
    };

    return data;
  }

  /**
   * Crappy endpoints that require 2 calls for creation (and it will be chained with a 3rd for save as draft, publish or schedule), it is what it is...
   */
  private createOrUpdate(data: PublicationPayload): Observable<{ publication: Publication }> {
    if (this.publicationToEdit) {
      return this.publicationsService.updatePublication(this.selectedSite.id, this.publicationToEdit.id, data);
    } else {
      return this.publicationsService
        .createPublication(this.selectedSite.id, this.parentPublication?.id)
        .pipe(
          mergeMap(({ publication_id }) =>
            this.publicationsService.updatePublication(this.selectedSite.id, publication_id, data)
          )
        );
    }
  }

  saveAsDraft() {
    this.basicService.showSpinner();
    this.createOrUpdate(this.prepareData())
      .pipe(
        mergeMap(({ publication }: { publication: Publication }) =>
          this.publicationsService.markAsDraft(this.selectedSite.id, publication.id)
        ),
        finalize(() => this.basicService.hideSpinner())
      )
      .subscribe(({ publication }: { publication: Publication }) => {
        this.basicService.toastSuccess('publication/edition/post-saved-as-draft');
        this.dialogRef.close(publication);
      });
  }

  schedule() {
    this.displayDateTimeModal = true;
  }

  confirmSchedule(dateTimeSchedule: DateTimeSchedule) {
    this.displayDateTimeModal = false;

    if (dateTimeSchedule) {
      const dialogRef = this.openConfirmation();

      dialogRef.afterClosed().subscribe(confirmation => {
        if (confirmation) {
          this.basicService.showSpinner();

          const { date, time, timezone } = dateTimeSchedule;
          const scheduledTime = DateUtils.createDate({
            year: date.get('year'),
            month: date.get('month'),
            date: date.get('date'),
            hour: time.h,
            minute: time.m,
            second: 0,
            timezone,
          });

          const data = this.prepareData();
          data.publication_time = scheduledTime.toISOString();

          this.checkAttachmentsUpload()
            .pipe(
              mergeMap(() => this.createOrUpdate(data)),
              mergeMap(({ publication }: { publication: Publication }) => {
                this.publicationToEdit = publication; // prevent creating 2nd publication if schedule fail and we save as draft after
                return this.publicationsService.schedule(this.selectedSite.id, publication.id);
              }),
              finalize(() => this.basicService.hideSpinner())
            )
            .subscribe({
              next: ({ publication }: { publication: Publication }) => {
                this.basicService.toastSuccess('publication/edition/post-successfully-scheduled');
                this.dialogRef.close(publication);
              },
              error: errorResponse => {
                // for schedule endpoint, concerns facebook
                if (!errorResponse.alcAlcreadyHandled) {
                  this.basicService.toastAlert('publication/edition/publication-not-scheduled-error', {
                    error: this.basicService.translate(errorResponse.error.error.error_message_translation_key),
                  });
                }
              },
            });
        }
      });
    }
  }

  /**
   * Only for twitter
   */
  private checkAttachmentsUpload(): Observable<string[]> {
    if (this.selectedSite.typeName === SiteTypeEnum.Facebook) {
      // When scheduling a Facebook publication, we are already sending it now,
      // and we'll already get the errors if there's any.
      // No need to do it twice.

      return of(['OK']);
    }

    const allAttachments: Editor2Attachment[] = this.form
      .get('editors')
      .value.reduce((output: Editor2Attachment[], editor: Editor2Answer) => [...output, ...editor.attachments], []);

    return from(allAttachments).pipe(
      concatMap(attachment => {
        const mediaId = attachment.type === 'picture' ? attachment.value.pictureId : attachment.value.videoId;
        return this.publicationsService.checkMediaUpload(this.selectedSite.id, mediaId, attachment.type);
      }),
      toArray()
    );
  }

  publishNow(isDuplication: boolean) {
    const dialogRef = this.openConfirmation();

    dialogRef.afterClosed().subscribe(confirmation => {
      if (confirmation) {
        this.basicService.showSpinner();

        this.createOrUpdate(this.prepareData())
          .pipe(
            mergeMap(({ publication }: { publication: Publication }) => {
              this.publicationToEdit = publication; // prevent creating 2nd publication if publish fail and we save as draft after
              return this.publicationsService.publishNow(this.selectedSite.id, publication.id);
            }),
            finalize(() => this.basicService.hideSpinner())
          )
          .subscribe({
            next: ({ publication }: { publication: Publication }) => {
              this.basicService.toastSuccess('publication/edition/post-successfully-published');

              if (isDuplication) {
                this.publicationToEdit = null;
                this.title = 'publications/edition/title/new';
                // TODO update list without clossing dialog (not done in AJS)
              } else {
                this.dialogRef.close(publication);
              }
            },
          });
      }
    });
  }

  private openConfirmation(): MatDialogRef<ConfirmDialogComponent> {
    return this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.basicService.translate('publications/list/confirm-publish-post', {
          siteLogo: `<span class="bg-img-${this.selectedSite.type_name} alc-site-icon-inline"></span>`,
          siteName: this.selectedSite.name,
        }),
        confirmBtnText: this.basicService.translate('ok'),
        cancelBtnText: this.basicService.translate('cancel'),
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
